<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import McGrapqlTable from '@/components/McGrapqlTable'
import { GET_VEHICLE_GROUPS, DELETE_VEHICLE_GROUP } from '@/services/api/setting/fleet/fleetGroup'
import { mapGetters } from 'vuex'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Setting',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McGrapqlTable,
  },
  data() {
    return {
      vgroups: [],
      selected: {
        fleetGroup: {},
      },
      refreshTable: false,
    }
  },
  computed: {
    /**
     * Total no. of records
     */
    title() {
      return this.$t('setting.list.fleetGroup.groupFleet')
    },

    subTitle() {
      return this.$t('setting.list.fleetGroup.textGroupFleet')
    },

    fleetGroup() {
      return [
        {
          key: 'name',
          label: this.$t('setting.list.fleetGroup.fleetGroupName'),
          thClass: 'mc-table--50',
          isSortable: true,
          shown: true,
        },
        {
          key: 'totalVehicle',
          label: this.$t('setting.list.fleetGroup.registerFleetTotal'),
          thClass: 'mc-table--50',
          isSortable: true,
          shown: true,
        },
      ]
    },

    vgroupApiClient() {
      return {
        get: async (filter) => {
          const res = await this.$apollo.query({
            query: GET_VEHICLE_GROUPS,
            variables: {
              filter: {
                search: filter.search,
              },
            },
            fetchPolicy: 'no-cache',
          })

          let items = res.data.vgroups.vgroups
          this.vgroups = items

          if (filter.orderBy && filter.orderBy.length > 0) {
            const sort = JSON.parse(filter.orderBy[0])
            const key = Object.keys(sort)[0]
            const order = sort[key]
            items.sort((a, b) => {
              let valA = a[key]
              let valB = b[key]
              if (order === 'asc') return valA > valB ? 1 : -1
              return valA < valB ? 1 : -1
            })
          }

          const page = filter.pagination.page
          const show = filter.pagination.show
          const paginatedItems = items.slice((page - 1) * show, page * show)

          return {
            data: paginatedItems,
            pagination: {
              page: page,
              total: items.length,
            },
          }
        },
      }
    },

    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),

    breadcrumbItems() {
      return [
        {
          text: this.$t('setting.homePage.pengaturan'),
          to: '/setting',
        },
        {
          text: this.$t('setting.list.fleetGroup.groupFleet'),
        },
      ]
    },
  },
  mounted() {},
  methods: {
    toDelete() {
      let promises = []
      const selectedIds = Object.keys(this.selected.fleetGroup).filter(
        (id) => this.selected.fleetGroup[id] === true
      )

      for (const id of selectedIds) {
        promises.push(
          this.$apollo.mutate({
            mutation: DELETE_VEHICLE_GROUP,
            variables: {
              input: {
                id: parseInt(id),
              },
            },
          })
        )
      }

      Promise.all(promises)
        .then(() => {
          this.refreshTable = true
          this.$nextTick(() => {
            this.refreshTable = false
          })
          this.clearChecked()
          this.$mcToast.success(this.$t('global.alert.text.delete'))
          this.$bvModal.hide('modal-delete-group')
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.log(e)
        })
    },

    onRowClicked(item) {
      if (
        !this.hasAccessAll([
          'vgroups:update',
          'c5a-default-setgrupkendaraan:read',
          'vehicles:read',
          'vehicle_categories:read',
        ])
      ) {
        return
      }
      this.$router.push({
        name: 'Fleet Group Detail',
        params: { id: item.id },
      })
    },

    clearChecked() {
      this.selected.fleetGroup = {}
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader
      :title="title"
      :subTitle="subTitle"
      :items="breadcrumbItems"
      hasBreadcrumb
      hasBackButton
    />

    <b-card class="mc-card__setting">
      <b-tabs content-class="p-3 text-muted" nav-wrapper-class="mc-tab">
        <!-- Grup Kendaraan -->
        <b-tab active class="border-0">
          <template #title>
            <span class="d-sm-inline-block"> {{ $t('setting.list.fleetGroup.groupFleet') }}</span>
          </template>

          <div class="clearfix mb-3 pb-3">
            <div class="float-end">
              <McButton
                v-if="
                  Object.keys(selected.fleetGroup).filter((id) => selected.fleetGroup[id] === true)
                    .length &&
                  hasAccessAll([
                    'vgroups:delete',
                    'c5a-default-setgrupkendaraan:read',
                    'vehicles:read',
                    'vehicle_categories:read',
                  ])
                "
                v-b-modal.modal-delete-group
                variant="secondary-red"
                class="mx-2"
                :text="$t('global.delete')"
                icon="icon-outline_trash"
                data-me="setting-fleetgroup-btn-delete"
              />
              <McButton
                v-if="
                  hasAccessAll([
                    'vgroups:create',
                    'c5a-default-setgrupkendaraan:read',
                    'vehicles:read',
                    'vehicle_categories:read',
                  ])
                "
                variant="primary"
                :text="$t('global.addField')"
                icon="icon-outline_plus"
                @click="$router.push({ name: 'Form Fleet Group' })"
                data-me="setting-fleetgroup-btn-add"
              />
            </div>
          </div>

          <!-- Table -->
          <McGrapqlTable
            :fields="fleetGroup"
            :apiClient="vgroupApiClient"
            :refreshData="refreshTable"
            :searchable="true"
            :hasDelete="false"
            @handleSelectValue="($event) => (selected.fleetGroup = $event)"
          >
            <template #cell(name)="data">
              <strong
                class="text-primary"
                @click="onRowClicked(data.item)"
                data-me="setting-fleetgroup-link-detail"
                >{{ data.item.name }}</strong
              >
            </template>
          </McGrapqlTable>
        </b-tab>
      </b-tabs>
    </b-card>

    <b-modal
      id="modal-delete-group"
      class="mc-modal"
      :title="$t('global.delete')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body mc-modal__filter p-2">
        <p>{{ $t('global.confirmDelete1') }} {{ $t('global.confirmDelete2') }}</p>
        <b-list-group>
          <b-list-group-item
            v-for="(id, index) in Object.keys(selected.fleetGroup).filter(
              (id) => selected.fleetGroup[id] === true
            )"
            :key="index"
          >
            {{ vgroups.find((g) => g.id == id) ? vgroups.find((g) => g.id == id).name : '' }}
          </b-list-group-item>
        </b-list-group>
      </div>
      <template #modal-footer="{ cancel }">
        <b-row class="mc-modal__filter-footer mx-0">
          <b-col sm="12" md="12" class="mc-modal__filter-footer-btn">
            <McButton
              variant="secondary"
              :text="$t('global.no')"
              class="me-2"
              @click="cancel()"
              data-me="setting-fleetgroup-modal-delete-btn-no"
            />
            <McButton
              variant="primary"
              :text="$t('global.yes')"
              @click="toDelete('fleetGroup')"
              data-me="setting-fleetgroup-modal-delete-btn-yes"
            />
          </b-col>
        </b-row>
      </template>
    </b-modal>
  </Layout>
</template>
